<template>
  <div class="category-tag-wall">
    <div v-if="loading" class="category-tag-wall__grid">
      <div
        v-for="i in 8"
        :key="'sk-' + i"
        class="category-tag-card category-tag-card--skeleton"
      />
    </div>

    <div v-else-if="disabled" class="category-tag-wall__empty">
      <p class="category-tag-wall__empty-title">请先选择图谱</p>
      <p class="category-tag-wall__empty-desc">
        选择图谱后可查看并维护该图谱下的类别
      </p>
    </div>

    <div v-else-if="!categories.length" class="category-tag-wall__empty">
      <p class="category-tag-wall__empty-title">{{ emptyTitle }}</p>
      <p class="category-tag-wall__empty-desc">
        点击下方「新建类别」添加第一个类型
      </p>
      <el-button type="primary" @click="$emit('create')">新建类别</el-button>
    </div>

    <div
      v-else-if="hasKeyword && !hasAnyMatch"
      class="category-tag-wall__empty"
    >
      <p class="category-tag-wall__empty-title">无匹配的类别</p>
      <p class="category-tag-wall__empty-desc">
        请调整搜索关键词，或清空搜索查看全部类别
      </p>
    </div>

    <div v-else class="category-tag-wall__grid">
      <div
        v-for="item in categories"
        :key="item.id"
        class="category-tag-card"
        :class="cardClasses(item)"
        :style="{ '--tag-accent': accentColor(item.code) }"
        role="button"
        tabindex="0"
        @click="$emit('edit', item)"
        @keydown.enter="$emit('edit', item)"
      >
        <div class="category-tag-card__accent" aria-hidden="true" />
        <div class="category-tag-card__head">
          <el-tooltip :content="item.name" placement="top" :show-after="400">
            <span class="category-tag-card__name">{{ item.name }}</span>
          </el-tooltip>
          <el-dropdown
            trigger="click"
            @click.stop
            @command="(cmd) => onCommand(cmd, item)"
          >
            <el-button
              class="category-tag-card__more"
              link
              :icon="MoreFilled"
              aria-label="更多操作"
              @click.stop
            />
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="edit">编辑</el-dropdown-item>
                <el-dropdown-item command="delete">
                  <span class="category-tag-card__danger">删除</span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
        <el-tooltip :content="item.code" placement="top" :show-after="400">
          <span class="category-tag-card__code">{{ item.code }}</span>
        </el-tooltip>
        <span
          class="category-tag-card__usage"
          :class="{ 'is-zero': !usageCount(item) }"
        >
          {{ formatUsage(item) }}
        </span>
      </div>

      <button
        type="button"
        class="category-tag-card category-tag-card--add"
        :disabled="disabled"
        @click="$emit('create')"
      >
        <el-icon class="category-tag-card__add-icon"><Plus /></el-icon>
        <span>新建类别</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps } from "vue";
import { Plus, MoreFilled } from "@element-plus/icons-vue";

const ACCENT_PALETTE = [
  "#4e6ef2",
  "#00b42a",
  "#ff7d00",
  "#f53f3f",
  "#722ed1",
  "#14c9c9",
  "#eb2f96",
  "#86909c",
];

const props = defineProps({
  categories: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  searchKeyword: {
    type: String,
    default: "",
  },
  emptyTitle: {
    type: String,
    default: "暂无实体类别",
  },
  usageCountKey: {
    type: String,
    default: "entityCount",
  },
  usageNoun: {
    type: String,
    default: "实体",
  },
});

const emit = defineEmits(["create", "edit", "delete"]);

const normalizedKeyword = computed(() =>
  (props.searchKeyword || "").trim().toLowerCase()
);

const hasKeyword = computed(() => normalizedKeyword.value.length > 0);

const hasAnyMatch = computed(() =>
  props.categories.some((item) => matchesKeyword(item))
);

function matchesKeyword(item) {
  if (!hasKeyword.value) {
    return true;
  }
  const kw = normalizedKeyword.value;
  const name = (item.name || "").toLowerCase();
  const code = (item.code || "").toLowerCase();
  return name.includes(kw) || code.includes(kw);
}

function cardClasses(item) {
  if (!hasKeyword.value) {
    return [];
  }
  return matchesKeyword(item) ? ["is-highlight"] : ["is-dimmed"];
}

function accentColor(code) {
  const text = code || "";
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash);
  }
  return ACCENT_PALETTE[Math.abs(hash) % ACCENT_PALETTE.length];
}

function usageCount(item) {
  return Number(item?.[props.usageCountKey]) || 0;
}

function formatUsage(item) {
  const n = usageCount(item);
  const noun = props.usageNoun;
  return n === 0 ? `暂无${noun}引用` : `${n} 个${noun}`;
}

function onCommand(command, item) {
  if (command === "edit") {
    emit("edit", item);
  } else if (command === "delete") {
    emit("delete", item);
  }
}
</script>
